import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Paper,
  Button,
  ButtonGroup,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart,
  ScatterChart,
  Scatter,
  FunnelChart,
  Funnel,
  LabelList
} from 'recharts';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Visibility as ViewsIcon,
  Favorite as LikesIcon,
  Comment as CommentsIcon,
  Share as SharesIcon,
  TouchApp as ClicksIcon,
  AttachMoney as RevenueIcon,
  Person as FollowersIcon,
  Public as ReachIcon,
  Speed as EngagementIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Refresh as RefreshIcon,
  DateRange as DateRangeIcon,
  FilterList as FilterIcon,
  TableChart as TableIcon,
  ShowChart as ChartIcon,
  Instagram,
  Twitter,
  Facebook,
  LinkedIn,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Language as LanguageIcon,
  DevicesOther as DeviceIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useDemoMode } from '../providers/DemoModeProvider';
import { campaignApi } from '../services/campaignApi';
import { analyticsApi, trackAnalyticsEvent } from '../services/analyticsApi';
import { getPlatformIcon, getPlatformColor } from '../utils/platformUtils';
import TwitterIcon from "@mui/icons-material/Twitter";

// Color schemes for charts
const CHART_COLORS = {
  primary: ['#1976d2', '#42a5f5', '#90caf9', '#e3f2fd'],
  engagement: ['#4caf50', '#66bb6a', '#81c784', '#a5d6a7'],
  platforms: {
    instagram: '#E4405F',
    twitter: '#1DA1F2',
    facebook: '#1877f2',
    linkedin: '#0077b5',
    tiktok: '#000000'
  },
  performance: ['#ff9800', '#ffb74d', '#ffcc02', '#ffe082'],
  conversion: ['#9c27b0', '#ba68c8', '#ce93d8', '#e1bee7']
};

// Tab panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Custom metric card component
const MetricCard = ({ title, value, previousValue, icon, trend, subtitle, color = 'primary' }) => {
  const theme = useTheme();
  const changePercent = previousValue ? ((value - previousValue) / previousValue * 100) : 0;
  const isPositive = changePercent > 0;
  const isFlat = Math.abs(changePercent) < 0.1;
  
  const getTrendIcon = () => {
    if (isFlat) return <TrendingFlatIcon fontSize="small" />;
    return isPositive ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />;
  };
  
  const getTrendColor = () => {
    if (isFlat) return 'text.secondary';
    return isPositive ? 'success.main' : 'error.main';
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Typography color="text.secondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{ mb: 1 }}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            {previousValue && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', color: getTrendColor() }}>
                  {getTrendIcon()}
                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                    {Math.abs(changePercent).toFixed(1)}%
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  vs last period
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: `${color}.light`,
              color: `${color}.main`,
              width: 56,
              height: 56
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
};

// Platform performance table component
const PlatformPerformanceTable = ({ data, loading }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Platform</TableCell>
            <TableCell align="right">Posts</TableCell>
            <TableCell align="right">Impressions</TableCell>
            <TableCell align="right">Engagement</TableCell>
            <TableCell align="right">Engagement Rate</TableCell>
            <TableCell align="right">Clicks</TableCell>
            <TableCell align="right">CTR</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((row) => (
            <TableRow key={row.platform}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar
                    sx={{
                      bgcolor: CHART_COLORS.platforms[row.platform] || '#gray',
                      width: 24,
                      height: 24
                    }}
                  >
                    {React.createElement(getPlatformIcon(row.platform) || TwitterIcon)}
                  </Avatar>
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                    {row.platform}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell align="right">{row.posts.toLocaleString()}</TableCell>
              <TableCell align="right">{row.impressions.toLocaleString()}</TableCell>
              <TableCell align="right">{row.engagement.toLocaleString()}</TableCell>
              <TableCell align="right">
                <Chip
                  label={`${row.engagementRate.toFixed(2)}%`}
                  color={row.engagementRate > 3 ? 'success' : row.engagementRate > 1 ? 'warning' : 'default'}
                  size="small"
                />
              </TableCell>
              <TableCell align="right">{row.clicks.toLocaleString()}</TableCell>
              <TableCell align="right">{row.ctr.toFixed(2)}%</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
      />
    </TableContainer>
  );
};

// Main Campaign Analytics Page Component
const CampaignAnalyticsPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { isDemoMode } = useDemoMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [dateRange, setDateRange] = useState('30d'); // 7d, 30d, 90d, custom
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const [refreshing, setRefreshing] = useState(false);
  const [dataSource, setDataSource] = useState('mock'); // 'real', 'mock', 'demo'

  // Mock data for demonstration (replace with real API calls)
  const mockAnalyticsData = useMemo(() => ({
    overview: {
      totalPosts: 24,
      totalImpressions: 425000,
      totalEngagement: 28500,
      totalClicks: 14200,
      totalReach: 320000,
      totalFollowers: 2850,
      avgEngagementRate: 6.7,
      totalRevenue: 89600,
      conversions: 342
    },
    previousPeriod: {
      totalPosts: 18,
      totalImpressions: 320000,
      totalEngagement: 21200,
      totalClicks: 10800,
      totalReach: 245000,
      totalFollowers: 2420,
      avgEngagementRate: 6.6,
      totalRevenue: 65400,
      conversions: 258
    },
    performanceTimeline: [
      { date: '2024-01-15', impressions: 18000, engagement: 1350, clicks: 620, reach: 14200 },
      { date: '2024-01-16', impressions: 22000, engagement: 1580, clicks: 750, reach: 17800 },
      { date: '2024-01-17', impressions: 25000, engagement: 1820, clicks: 890, reach: 19500 },
      { date: '2024-01-18', impressions: 28000, engagement: 2100, clicks: 980, reach: 21600 },
      { date: '2024-01-19', impressions: 32000, engagement: 2450, clicks: 1150, reach: 24800 },
      { date: '2024-01-20', impressions: 35000, engagement: 2680, clicks: 1280, reach: 27200 },
      { date: '2024-01-21', impressions: 38000, engagement: 2890, clicks: 1420, reach: 29800 }
    ],
    platformBreakdown: [
      {
        platform: 'instagram',
        posts: 8,
        impressions: 125000,
        engagement: 8950,
        engagementRate: 7.2,
        clicks: 4200,
        ctr: 3.36,
        revenue: 28500
      },
      {
        platform: 'twitter',
        posts: 6,
        impressions: 95000,
        engagement: 6800,
        engagementRate: 7.2,
        clicks: 3150,
        ctr: 3.32,
        revenue: 21800
      },
      {
        platform: 'linkedin',
        posts: 6,
        impressions: 110000,
        engagement: 7950,
        engagementRate: 7.2,
        clicks: 3680,
        ctr: 3.35,
        revenue: 24600
      },
      {
        platform: 'facebook',
        posts: 4,
        impressions: 95000,
        engagement: 4800,
        engagementRate: 5.1,
        clicks: 3170,
        ctr: 3.34,
        revenue: 14700
      }
    ],
    topPosts: [
      {
        id: 1,
        content: "🚀 TechFlow Pro v2.0 is LIVE! Experience the future of project management with AI-powered automation...",
        platform: 'linkedin',
        impressions: 18500,
        engagement: 1850,
        engagementRate: 10.0,
        publishedAt: '2024-01-16T10:00:00Z'
      },
      {
        id: 2,
        content: "Behind the scenes: How we built AI automation that saves teams 15+ hours per week 🤖⚡",
        platform: 'instagram',
        impressions: 15200,
        engagement: 1420,
        engagementRate: 9.3,
        publishedAt: '2024-01-18T14:30:00Z'
      },
      {
        id: 3,
        content: "Customer success story: @StartupCo increased productivity by 300% using TechFlow Pro's new features 📈",
        platform: 'twitter',
        impressions: 12800,
        engagement: 1050,
        engagementRate: 8.2,
        publishedAt: '2024-01-20T09:15:00Z'
      }
    ],
    audienceInsights: {
      demographics: {
        ageGroups: [
          { age: '18-24', percentage: 15 },
          { age: '25-34', percentage: 35 },
          { age: '35-44', percentage: 28 },
          { age: '45-54', percentage: 15 },
          { age: '55+', percentage: 7 }
        ],
        gender: [
          { gender: 'Female', percentage: 52 },
          { gender: 'Male', percentage: 45 },
          { gender: 'Other', percentage: 3 }
        ]
      },
      locations: [
        { country: 'United States', percentage: 42 },
        { country: 'United Kingdom', percentage: 18 },
        { country: 'Canada', percentage: 12 },
        { country: 'Germany', percentage: 8 },
        { country: 'Other', percentage: 20 }
      ],
      devices: [
        { device: 'Mobile', percentage: 68 },
        { device: 'Desktop', percentage: 24 },
        { device: 'Tablet', percentage: 8 }
      ]
    },
    conversionFunnel: [
      { stage: 'Impressions', value: 425000, color: '#1976d2' },
      { stage: 'Clicks', value: 14200, color: '#42a5f5' },
      { stage: 'Visits', value: 11850, color: '#90caf9' },
      { stage: 'Leads', value: 1680, color: '#e3f2fd' },
      { stage: 'Conversions', value: 342, color: '#bbdefb' }
    ]
  }), []);

  // Load analytics data
  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Track analytics page view
        trackAnalyticsEvent('campaign_analytics_viewed', {
          campaignId,
          dateRange,
          isDemo: isDemoMode || campaignId === 'demo-campaign-2024'
        });
        
        // Check if demo mode is enabled or this is the demo campaign
        if (isDemoMode || campaignId === 'demo-campaign-2024') {
          // Load demo campaign data immediately
          console.log('🎬 Demo mode enabled or demo campaign, using mock analytics data');
          setCampaign({ 
            name: '🚀 TechFlow Pro Product Launch Campaign', 
            status: 'active',
            description: 'Comprehensive product launch campaign for TechFlow Pro v2.0, featuring new AI-powered automation tools, enhanced collaboration features, and mobile app launch.',
            businessProfile: {
              businessName: 'TechFlow Pro',
              industry: 'Software Development'
            }
          });
          setAnalyticsData(mockAnalyticsData);
          setDataSource('demo');
          setLoading(false);
          return;
        }
        
        // Load campaign details for real campaigns
        const campaignResponse = await campaignApi.getCampaign(campaignId);
        setCampaign(campaignResponse.data);
        
        // Load real analytics data from backend
        try {
          const analyticsResponse = await analyticsApi.getDashboard(campaignId);
          
          if (analyticsResponse.success && analyticsResponse.data) {
            setAnalyticsData(analyticsResponse.data);
            setDataSource('real');
            console.log('✅ Loaded real analytics data:', analyticsResponse.data);
          } else {
            throw new Error('Invalid analytics response');
          }
        } catch (analyticsError) {
          console.warn('⚠️ Analytics API failed, falling back to mock data:', analyticsError.message);
          // Fallback to mock data for better user experience
          setAnalyticsData(mockAnalyticsData);
          setDataSource('mock');
        }
        
      } catch (err) {
        console.error('Failed to load analytics:', err);
        setError('Failed to load analytics data');
        // Use mock data as fallback
        setAnalyticsData(mockAnalyticsData);
        setDataSource('mock');
        setCampaign({ name: 'Sample Campaign', status: 'active' });
      } finally {
        setLoading(false);
      }
    };

    if (campaignId) {
      loadAnalytics();
    }
  }, [campaignId, dateRange, mockAnalyticsData]);

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    
    // Track refresh action
    trackAnalyticsEvent('analytics_refresh_clicked', {
      campaignId,
      dateRange
    });
    // Simulate API refresh
    await new Promise(resolve => setTimeout(resolve, 1000));
    setRefreshing(false);
  };

  // Export functionality
  const handleExport = async () => {
    try {
      const exportData = {
        campaign: campaign?.name,
        dateRange,
        exportedAt: new Date().toISOString(),
        data: analyticsData
      };

      if (exportFormat === 'csv') {
        // Convert to CSV format
        const csvContent = convertToCSV(exportData);
        downloadFile(csvContent, `campaign-analytics-${campaignId}.csv`, 'text/csv');
      } else if (exportFormat === 'json') {
        // Export as JSON
        const jsonContent = JSON.stringify(exportData, null, 2);
        downloadFile(jsonContent, `campaign-analytics-${campaignId}.json`, 'application/json');
      }
      
      setExportDialogOpen(false);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  // Helper functions
  const convertToCSV = (data) => {
    const headers = ['Metric', 'Value', 'Previous Period', 'Change %'];
    const rows = [
      ['Total Posts', data.data.overview.totalPosts, data.data.previousPeriod.totalPosts, calculateChange(data.data.overview.totalPosts, data.data.previousPeriod.totalPosts)],
      ['Total Impressions', data.data.overview.totalImpressions, data.data.previousPeriod.totalImpressions, calculateChange(data.data.overview.totalImpressions, data.data.previousPeriod.totalImpressions)],
      ['Total Engagement', data.data.overview.totalEngagement, data.data.previousPeriod.totalEngagement, calculateChange(data.data.overview.totalEngagement, data.data.previousPeriod.totalEngagement)],
      // Add more rows as needed
    ];
    
    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  const calculateChange = (current, previous) => {
    if (!previous) return '0%';
    const change = ((current - previous) / previous * 100).toFixed(1);
    return `${change}%`;
  };

  const downloadFile = (content, filename, contentType) => {
    const blob = new Blob([content], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error && !analyticsData) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h4" component="h1">
                Campaign Analytics
              </Typography>
              {(isDemoMode || campaignId === 'demo-campaign-2024') && (
                <Chip 
                  label="DEMO" 
                  size="small" 
                  color="primary" 
                  sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
                />
              )}
            </Box>
            <Typography variant="subtitle1" color="text.secondary">
              {campaign?.name || 'Loading...'}
            </Typography>
            {(isDemoMode || campaignId === 'demo-campaign-2024') && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                🎬 Demo Mode: This is sample analytics data showcasing SoloSocial's campaign tracking capabilities
              </Typography>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                label="Date Range"
              >
                <MenuItem value="7d">Last 7 days</MenuItem>
                <MenuItem value="30d">Last 30 days</MenuItem>
                <MenuItem value="90d">Last 90 days</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing}
            >
              Refresh
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => setExportDialogOpen(true)}
            >
              Export
            </Button>
          </Box>
        </Box>
        
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Using sample data due to connection issues
          </Alert>
        )}

        {/* Data Source Indicator */}
        {!loading && (
          <Alert 
            severity={dataSource === 'real' ? 'success' : dataSource === 'demo' ? 'info' : 'warning'} 
            sx={{ mb: 2 }}
          >
            {dataSource === 'real' && (
              <Typography variant="body2">
                <strong>Live Data:</strong> Showing real analytics data from your campaigns
              </Typography>
            )}
            {dataSource === 'demo' && (
              <Typography variant="body2">
                <strong>Demo Campaign:</strong> Explore analytics features with sample data from TechFlow Pro
              </Typography>
            )}
            {dataSource === 'mock' && (
              <Typography variant="body2">
                <strong>Sample Data:</strong> Analytics backend is being set up. Showing representative data for UI preview
              </Typography>
            )}
          </Alert>
        )}
      </Box>

      {/* Overview Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Impressions"
            value={analyticsData?.overview.totalImpressions}
            previousValue={analyticsData?.previousPeriod.totalImpressions}
            icon={<ViewsIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Engagement"
            value={analyticsData?.overview.totalEngagement}
            previousValue={analyticsData?.previousPeriod.totalEngagement}
            icon={<EngagementIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Engagement Rate"
            value={`${analyticsData?.overview.avgEngagementRate}%`}
            previousValue={analyticsData?.previousPeriod.avgEngagementRate}
            icon={<TrendingUpIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Revenue"
            value={`$${analyticsData?.overview.totalRevenue?.toLocaleString()}`}
            previousValue={analyticsData?.previousPeriod.totalRevenue}
            icon={<RevenueIcon />}
            color="secondary"
          />
        </Grid>
      </Grid>

      {/* Analytics Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Performance" icon={<ChartIcon />} />
          <Tab label="Platform Breakdown" icon={<TableIcon />} />
          <Tab label="Content Analysis" icon={<AssignmentIcon />} />
          <Tab label="Audience Insights" icon={<LocationIcon />} />
          <Tab label="ROI Analysis" icon={<RevenueIcon />} />
        </Tabs>

        {/* Performance Tab */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Performance Timeline
                  </Typography>
                  <ResponsiveContainer width="100%" height={400}>
                    <ComposedChart data={analyticsData?.performanceTimeline}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <RechartsTooltip />
                      <Legend />
                      <Bar yAxisId="left" dataKey="impressions" fill={CHART_COLORS.primary[0]} name="Impressions" />
                      <Line yAxisId="right" type="monotone" dataKey="engagement" stroke={CHART_COLORS.engagement[0]} name="Engagement" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Platform Breakdown Tab */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Platform Performance
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analyticsData?.platformBreakdown}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="engagement"
                        label={({ platform, engagement }) => `${platform}: ${engagement}`}
                      >
                        {analyticsData?.platformBreakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS.platforms[entry.platform]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Platform Comparison
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <RadarChart data={analyticsData?.platformBreakdown}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="platform" />
                      <PolarRadiusAxis />
                      <Radar
                        name="Engagement Rate"
                        dataKey="engagementRate"
                        stroke={CHART_COLORS.engagement[0]}
                        fill={CHART_COLORS.engagement[0]}
                        fillOpacity={0.3}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Detailed Platform Metrics
                  </Typography>
                  <PlatformPerformanceTable
                    data={analyticsData?.platformBreakdown || []}
                    loading={loading}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Content Analysis Tab */}
        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Top Performing Posts
                  </Typography>
                  <List>
                    {analyticsData?.topPosts.map((post, index) => (
                      <React.Fragment key={post.id}>
                        <ListItem>
                          <ListItemIcon>
                            <Avatar
                              sx={{
                                bgcolor: CHART_COLORS.platforms[post.platform],
                                width: 32,
                                height: 32
                              }}
                            >
                              {React.createElement(getPlatformIcon(post.platform) || TwitterIcon)}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={post.content.substring(0, 80) + '...'}
                            secondary={
                              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                <Chip
                                  label={`${post.impressions.toLocaleString()} views`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                                <Chip
                                  label={`${post.engagement.toLocaleString()} engagement`}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                                <Chip
                                  label={`${post.engagementRate}% rate`}
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < analyticsData.topPosts.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Audience Insights Tab */}
        <TabPanel value={activeTab} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Age Demographics
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analyticsData?.audienceInsights.demographics.ageGroups}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="age" />
                      <YAxis />
                      <RechartsTooltip />
                      <Bar dataKey="percentage" fill={CHART_COLORS.primary[0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Geographic Distribution
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analyticsData?.audienceInsights.locations}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="percentage"
                        label={({ country, percentage }) => `${country}: ${percentage}%`}
                      >
                        {analyticsData?.audienceInsights.locations.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS.primary[index % CHART_COLORS.primary.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Device Breakdown
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analyticsData?.audienceInsights.devices}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="percentage"
                        label={({ device, percentage }) => `${device}: ${percentage}%`}
                      >
                        {analyticsData?.audienceInsights.devices.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS.engagement[index % CHART_COLORS.engagement.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* ROI Analysis Tab */}
        <TabPanel value={activeTab} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Conversion Funnel
                  </Typography>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={analyticsData?.conversionFunnel}
                      layout="horizontal"
                      margin={{ left: 100 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="stage" />
                      <RechartsTooltip />
                      <Bar dataKey="value" fill={CHART_COLORS.conversion[0]}>
                        <LabelList dataKey="value" position="right" />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Revenue Metrics
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography>Total Revenue:</Typography>
                      <Typography fontWeight="bold">
                        ${analyticsData?.overview.totalRevenue.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography>Cost per Click:</Typography>
                      <Typography fontWeight="bold">$7.43</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography>Return on Ad Spend:</Typography>
                      <Typography fontWeight="bold" color="success.main">420%</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography>Conversion Rate:</Typography>
                      <Typography fontWeight="bold">2.1%</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Platform ROI
                  </Typography>
                  <List>
                    {analyticsData?.platformBreakdown.map((platform) => (
                      <ListItem key={platform.platform}>
                        <ListItemIcon>
                          <Avatar
                            sx={{
                              bgcolor: CHART_COLORS.platforms[platform.platform],
                              width: 24,
                              height: 24
                            }}
                          >
                            {React.createElement(getPlatformIcon(platform.platform) || TwitterIcon)}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={platform.platform.charAt(0).toUpperCase() + platform.platform.slice(1)}
                          secondary={`Revenue: $${platform.revenue?.toLocaleString()}`}
                        />
                        <Chip
                          label={`${((platform.revenue / analyticsData.overview.totalRevenue) * 100).toFixed(1)}%`}
                          color="primary"
                          size="small"
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
        <DialogTitle>Export Analytics Data</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Export Format</InputLabel>
            <Select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
              label="Export Format"
            >
              <MenuItem value="csv">CSV (Comma Separated Values)</MenuItem>
              <MenuItem value="json">JSON (JavaScript Object Notation)</MenuItem>
              <MenuItem value="pdf">PDF Report (Coming Soon)</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleExport} variant="contained">Export</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CampaignAnalyticsPage; 